import type React from 'react';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { PopupBlockedDialog } from '../components/PopupBlockedDialog/PopupBlockedDialog';
import {
  type MeetingWindowAuthProvider,
  openMeetingWindow,
} from '../services/meetingWindow/meetingWindowManager';

/**
 * Details the launch site hands over when the meeting popup is blocked, so the
 * dialog can retry the popup itself or fall back to opening the meeting inline.
 */
export interface PopupBlockedPromptRequest {
  meetingId: string;
  password?: string | null;
  /** Supplies host identity to a retried popup (same provider used originally). */
  getAuth: MeetingWindowAuthProvider;
  /**
   * Open the meeting inside the current window (caller owns the navigation).
   * Omitted where replacing the current window is not an option — the desktop
   * app, which would lose the TeamHub shell with no way back.
   */
  onOpenInPage?: () => void;
}

export interface PopupBlockedContextValue {
  /**
   * Show the "pop-ups are blocked" fallback dialog. Call this from the `blocked`
   * branch of a meeting launch instead of silently navigating in-page.
   */
  promptPopupBlocked: (request: PopupBlockedPromptRequest) => void;
}

const PopupBlockedContext = createContext<PopupBlockedContextValue | undefined>(undefined);

export interface PopupBlockedProviderProps {
  children: ReactNode;
}

export const PopupBlockedProvider: React.FC<PopupBlockedProviderProps> = ({ children }) => {
  const [request, setRequest] = useState<PopupBlockedPromptRequest | null>(null);

  const promptPopupBlocked = useCallback((next: PopupBlockedPromptRequest) => {
    setRequest(next);
  }, []);

  const handleCancel = useCallback(() => {
    setRequest(null);
  }, []);

  const handleOpenInPage = useCallback(() => {
    request?.onOpenInPage?.();
    setRequest(null);
  }, [request]);

  const handleRetry = useCallback(() => {
    if (!request) {
      return;
    }
    // This click is a fresh user gesture, so the popup can succeed even though
    // the original launch was blocked. Keep the dialog open only if it fails
    // again so the user can still fall back to opening in this window.
    const result = openMeetingWindow({
      meetingId: request.meetingId,
      password: request.password,
      getAuth: request.getAuth,
    });
    if (result.status !== 'blocked') {
      setRequest(null);
    }
  }, [request]);

  const value = useMemo<PopupBlockedContextValue>(
    () => ({ promptPopupBlocked }),
    [promptPopupBlocked],
  );

  return (
    <PopupBlockedContext.Provider value={value}>
      {children}
      {request ? (
        <PopupBlockedDialog
          onRetry={handleRetry}
          onOpenInPage={request.onOpenInPage ? handleOpenInPage : undefined}
          onCancel={handleCancel}
        />
      ) : null}
    </PopupBlockedContext.Provider>
  );
};

export const usePopupBlocked = (): PopupBlockedContextValue => {
  const context = useContext(PopupBlockedContext);
  if (context === undefined) {
    throw new Error('usePopupBlocked must be used within a PopupBlockedProvider');
  }
  return context;
};
